import React from 'react'
import Breadcrumbs from 'components/Common/Breadcrumb';
import { Button, Card, CardBody, Col, Container, FormGroup, Label, Row } from 'reactstrap';
import { MetaTags } from 'react-meta-tags';
import { AppConfiguration } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import { getDmsStatsData, getDmsLogsData, getDmsLogsStreams } from 'store/admin/dev-dashboard/actions';
import { useState } from 'react';
import ReactMultiselectCheckboxes from 'react-multiselect-checkboxes/lib/ReactMultiselectCheckboxes';
import { useEffect } from 'react';
import CustomDateRangePicker, { createOption, TIME_RANGE_OPTIONS } from 'components/Common/DateRangePicker';
import { getAppropriateLoadingGif, getIANATimezoneStringForAbbreviation, getPrimaryButtonClass, SfCommaStringFormatter } from 'helpers/sf_utils';
import { DMS_LOGS_STATUS_OPTIONS, REPORTING_TIMEZONE } from 'helpers/sf_constants';
import Select from "react-select";
import StatsCard from '../DevDashboard/StatsCard';
import DmsDashboardTable from './DmsDashboardTable';

const DEFAULT_PAGE_SIZE = 30
const DEFAULT_SORT = {
  sort_column: 'timestamp',
  sort_direction: 'desc',
}
const DEFAULT_LOG_STREAM_OPTIONS = [
  { label: 'All', value: 0 },
]

const DevDashboard = () => {
  const {
    current_user,
    dms_logs_streams,
    dms_logs_streams_loading,
    dms_stats_data,
    dms_stats_data_loading
  } = useSelector((state) => ({
    current_user: state.LoginAdminUser.current_user,
    dms_logs_streams: state.DevDashboard.dms_logs_streams,
    dms_stats_data: state.DevDashboard.dms_stats_data,
    dms_logs_streams_loading: state.DevDashboard.dms_logs_streams_loading,
    dms_stats_data_loading: state.DevDashboard.dms_stats_data_loading
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDmsLogsStreams());
    applyFilters([])
    applyFiltersOnDmsLogsData([])
  }, [])

  const getDmsLogStreams = (dms_logs_streams) => {
    return dms_logs_streams?.reduce((a, c) => {
      return [
        ...a,
        {
          label: c?.log_stream,
          value: c?.log_stream
        }
      ]
    }, DEFAULT_LOG_STREAM_OPTIONS)

  }

  const getDropdownLabelForProfilingTags = ({ placeholderButtonLabel, value }) => {
    if (value && value?.some((o) => o.value === 0)) {
      return <span className='css-1v99tuv text-black align-self-center'>All</span>;
    }
    return <span className='css-1v99tuv text-black align-self-center'>{value?.length} selected</span>;
  }

  const handleSelectLogStream = (selectedOption, event) => {
    const options = getDmsLogStreams(dms_logs_streams)
    if (event.action === 'select-option' && event.option.value === 0) {
      setSelectedLogStream(options);
    } else if (
      event.action === 'deselect-option'
      && event.option.value === 0
    ) {
      setSelectedLogStream([]);
    } else if (event.action === 'deselect-option') {
      setSelectedLogStream(selectedOption.filter((o) => o.value !== 0));
    } else if (selectedOption.length === options.length - 1) {
      setSelectedLogStream(options);
    } else {
      setSelectedLogStream(selectedOption);
    }
  }

  //states
  const [selectedLogStream, setSelectedLogStream] = useState([])
  const [selectedDateRange, setSelectedDateRange] = useState(createOption(
    TIME_RANGE_OPTIONS.LAST_HOUR,
    new Date(),
    getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE),
  ))
  const [selectedType, setSelectedType] = useState(DMS_LOGS_STATUS_OPTIONS[0]);
  const [dmsLogsSort, setDmsLogsSort] = useState(DEFAULT_SORT);
  const [appliedDmsLogsFilters, setAppliedDmsLogsFilters] = useState(null);

  useEffect(() => {
    setSelectedLogStream(getDmsLogStreams(dms_logs_streams))
  }, [dms_logs_streams])

  const preparePayload = (selectedLogStream) => {
    const selectedLogStreamValues = selectedLogStream?.map((tag) => tag.value)

    const data = {
      start_date: selectedDateRange?.value?.startDate,
      end_date: selectedDateRange?.value?.endDate,
      message_type: selectedType?.value,
      log_stream: selectedLogStreamValues.includes(0) ? [] : selectedLogStreamValues
    }
    if (selectedType?.value === 'All') {
      delete data.message_type
    }
    return data
  }

  const applyFilters = (selectedLogStream) => {
    const data = preparePayload(selectedLogStream)
    dispatch(getDmsStatsData(data));
  }

  const buildDmsLogsPayload = (filters, pageNumber = 1, sortOptions = dmsLogsSort) => {
    return {
      ...filters,
      page_number: pageNumber,
      page_size: DEFAULT_PAGE_SIZE,
      sort_column: sortOptions.sort_column,
      sort_direction: sortOptions.sort_direction,
    }
  }

  const applyFiltersOnDmsLogsData = (selectedLogStream) => {
    const filters = preparePayload(selectedLogStream)
    setAppliedDmsLogsFilters(filters)
    dispatch(getDmsLogsData(buildDmsLogsPayload(filters)))
  }

  const fetchDmsLogsPage = (pageNumber) => {
    const filters = appliedDmsLogsFilters || preparePayload(selectedLogStream)
    dispatch(getDmsLogsData(buildDmsLogsPayload(filters, Math.max(1, Number(pageNumber) || 1))))
  }

  const onDmsLogsSortChange = (sort_column, sort_direction) => {
    const nextSort = { sort_column, sort_direction }
    setDmsLogsSort(nextSort)
    const filters = appliedDmsLogsFilters || preparePayload(selectedLogStream)
    dispatch(getDmsLogsData(buildDmsLogsPayload(filters, 1, nextSort)))
  }

  return (
    <div className="page-content" id="page-content-wrapper">
      <MetaTags>
        <title>DMS Logs| {AppConfiguration.WEBSITE}</title>
      </MetaTags>
      {dms_logs_streams_loading ? getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)
        : <Container fluid>
          <Breadcrumbs title="" breadcrumbItem="DMS Logs" />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <Row className='d-flex flex-row'>
                    <Col className="align-self-end col-2">
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Log Stream</Label>
                        <div>
                          <ReactMultiselectCheckboxes
                            hideSearch
                            getDropdownButtonLabel={getDropdownLabelForProfilingTags}
                            isMulti
                            value={selectedLogStream}
                            onChange={(selectedOption, event) => {
                              handleSelectLogStream(selectedOption, event);
                            }}
                            options={getDmsLogStreams(dms_logs_streams)}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md='2'>
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Time</Label>
                        <div>
                          <CustomDateRangePicker
                            ianaTz={getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE)}
                            value={selectedDateRange}
                            onChange={setSelectedDateRange}
                            options={Object.keys(TIME_RANGE_OPTIONS).map((opt) => createOption(
                              TIME_RANGE_OPTIONS[opt],
                              new Date(),
                              getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE),
                            ))}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md='2'>
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Type</Label>
                        <Select
                          name="elapsed_time_type"
                          options={DMS_LOGS_STATUS_OPTIONS}
                          onChange={setSelectedType}
                          value={selectedType}
                        />
                      </FormGroup>
                    </Col>
                    <Col className="align-self-end">
                      <div>
                        <FormGroup className="mb-0">
                          <Button
                            className={`btn ${getPrimaryButtonClass()}`}
                            onClick={() => {
                              applyFilters(selectedLogStream, getDmsStatsData)
                              applyFiltersOnDmsLogsData(selectedLogStream)
                            }}>
                            {dms_stats_data_loading ? 'Loading Data...' : 'Update'}
                          </Button>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <StatsCard 
                label='Warnings'
                aggr_data={SfCommaStringFormatter(dms_stats_data?.[0]?.num_warnings)}
                loading={dms_stats_data_loading}
              />
            </Col>
            <Col md={4}>
              <StatsCard 
                label='Errors'
                aggr_data={SfCommaStringFormatter(dms_stats_data?.[0]?.num_errors)}
                loading={dms_stats_data_loading}
              />
            </Col>
          </Row>
          <DmsDashboardTable 
            selectedLogStream={selectedLogStream}
            fetchDmsLogsPage={fetchDmsLogsPage}
            onDmsLogsSortChange={onDmsLogsSortChange}
            dmsLogsSort={dmsLogsSort}
          />
        </Container>}
    </div >
  )
}

export default DevDashboard
